import { useState, useEffect } from "react";
import { TextField, IconButton, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Tooltip } from "@mui/material";
import { Edit, Delete, Close } from "@mui/icons-material";
import { fetchAllEntities, postResource, deleteCommentEntities, postdatas } from "../../../../apis/apiService";
import Loader from "../../../../components/common/Loader";
import { useTheme } from "../../../../ThemeContext";
import { toast } from "react-toastify";
import "../../../../App.css"

const Awards = () => {
    const { isDarkMode } = useTheme();

    const [fields, setFields] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // const [periods, setPeriods] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, field: null });

    const pageTitleColor = isDarkMode ? "#f5f7fb" : "#1a1a2e";
    const pageSubtitleColor = isDarkMode ? "#9aa4b2" : "#6b7280";

    const cardBackground = isDarkMode
        ? "linear-gradient(135deg, rgba(22, 27, 34, 0.98), rgba(18, 22, 29, 0.95))"
        : "#fff";
    const cardBorder = isDarkMode ? "1px solid rgba(91, 108, 132, 0.38)" : "1px solid #e5e7eb";

    const cardShadow = isDarkMode
        ? "0 18px 40px rgba(0, 0, 0, 0.28)"
        : "0 1px 3px rgba(0,0,0,0.04)";

    const cardHoverShadow = isDarkMode
        ? "0 20px 44px rgba(6, 182, 212, 0.12)"
        : "0 2px 8px rgba(23,176,167,0.10)";
    const labelColor = isDarkMode ? "#c5d1de" : "#374151";
    const inputBackground = isDarkMode ? "rgba(10, 15, 22, 0.9)" : "#ffffff";
    const inputTextColor = isDarkMode ? "#f5f7fb" : "#1f2937";
    const inputBorderColor = isDarkMode ? "rgba(95, 112, 136, 0.55)" : "#d1d5db";
    const inputDisabledBackground = isDarkMode ? "rgba(15, 21, 29, 0.72)" : "#ffffff";
    const editButtonColor = isDarkMode ? "#63b3ff" : "#1976d2";
    const editButtonBackground = isDarkMode ? "rgba(33, 150, 243, 0.12)" : "transparent";
    const deleteButtonColor = isDarkMode ? "#ff7f7f" : "#e53935";
    const deleteButtonBackground = isDarkMode ? "rgba(239, 68, 68, 0.12)" : "transparent";

    // GET All Award Periods (for period_id dropdown)
    // const fetchAwardPeriods = async () => {
    //     try {
    //         const response = await fetchAllEntities("master/award-periods/show");
    //         if (response && response.data) {
    //             setPeriods(response.data);
    //         }
    //     } catch (err) {
    //         console.error("Error fetching award periods for dropdown:", err);
    //     }
    // };

    // GET All Awards
    const fetchAwards = async () => {
        setIsLoading(true);
        try {
            const response = await fetchAllEntities("master/awards/show");
            // console.log("Awards API response:", response);
            if (response && response.data) {
                const awardsArray = Array.isArray(response.data)
                    ? response.data
                    : Object.values(response.data);

                setFields(
                    awardsArray.map((award) => ({
                        id: award.id ?? award.award_id,
                        award_name: award.award_name,
                        description: award.description,
                        // period_id: award.period_id,
                        isNew: false,
                        isEditing: false,
                    }))
                );
            }
        } 
        catch (err) {
            console.error("Error fetching awards:", err);
            toast.error(err?.message || err?.response?.data?.message || "Failed to fetch awards.");
        } 
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // fetchAwardPeriods();
        fetchAwards();
    }, []);

    // Cancel / remove an unsaved new row
    const handleCancelNewField = (id) => {
        setFields((prev) => prev.filter((f) => f.id !== id));
    };

    const handleAddField = () => {
        setFields([...fields, {
            id: Date.now(),
            award_name: "",
            description: "",
            // period_id: "",
            isNew: true,
            isEditing: false,
        }]);
    };

    const handleFieldChange = (id, key, value) => {
        setFields((prev) => prev.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
    };

    const handleEditClick = (id) => {
        setFields((prev) => prev.map((f) => (f.id === id ? { ...f, isEditing: true } : f)));
    };

    const handleSaveNewField = async (id) => {
        const field = fields.find((f) => f.id === id);
        if (!field) return;
        if (!field.award_name.trim()) { toast.error("Award Name is required."); return; }
        // if (!field.period_id) { toast.error("Please select a Period."); return; }
        try {
            const response = await postdatas("master/awards", "create", {
                award_name: field.award_name,
                description: field.description,
                // period_id: field.period_id,
            });
            toast.success(response?.message || "Award created successfully.");
            fetchAwards();
        } catch (err) {
            console.error("Error creating award:", err);
            toast.error(err?.data?.message || err?.message || "Creation failed.");
        }
    };

    const handleUpdate = async (field) => {
        if (field.isNew) return;
        if (!field.award_name.trim()) { toast.error("Award Name is required."); return; }
        // if (!field.period_id) { toast.error("Please select a Period."); return; }
        try {
            const response = await postResource("master/awards", "update", field.id, {
                award_name: field.award_name,
                description: field.description,
                // period_id: field.period_id,
            });
            toast.success(response.message || "Award updated successfully.");
            setFields((prev) => prev.map((f) => f.id === field.id ? { ...f, isEditing: false } : f));
        } 
        catch (err) {
            console.error("Error updating award:", err);
            toast.error(err?.message || err?.response?.data?.message || "Update failed.");
        }
    };

    const handleBlur = (id) => {
        const field = fields.find((f) => f.id === id);
        if (field && !field.isNew && field.isEditing) handleUpdate(field);
    };

    const handleDeleteClick = (field) => {
        // console.log("Delete clicked for award:", field);
        setDeleteDialog({ open: true, field });
    };

    const handleDelete = async () => {
        if (!deleteDialog.field) return;
        const recordId = deleteDialog.field.id;
        if (!recordId) {
            toast.error("Invalid record ID. Cannot delete.");
            setDeleteDialog({ open: false, field: null });
            return;
        }
        try {
            const response = await deleteCommentEntities("master/awards/delete", recordId);
            toast.success(response.message || "Award deleted successfully.");
            setFields((prev) => prev.filter((f) => f.id !== recordId));
        } 
        catch (err) {
            console.error("Error deleting award:", err);
            toast.error(err?.message || err?.response?.data?.message || "Deletion failed.");
        } 
        finally {
            setDeleteDialog({ open: false, field: null });
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            {isLoading ? (
                <Loader />
            ) : (
                <Box sx={{ p: { xs: "12px 14px", sm: "24px 28px" }, maxWidth: "1200px", width: "100%", margin: "0 auto" }}>

                    {/* Header: Title left, Add button right */}
                    <Box 
                        display="flex" 
                        flexDirection={{ xs: "column", sm: "row" }}
                        justifyContent="space-between" 
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        gap={{ xs: 2.5, sm: 0 }}
                        mb={{ xs: 3, sm: 4 }}
                    >
                        <Box>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: "1.2rem", color: pageTitleColor }}>
                                Awards
                            </h4>
                            <p style={{ margin: "4px 0 0 0", color: pageSubtitleColor, fontSize: "0.85rem" }}>
                                Manage awards and recognition programs
                            </p>
                        </Box>
                        <button
                            type="button"
                            onClick={handleAddField}
                            className="add-btn"
                            style={{ 
                                padding: "8px 14px", 
                                fontSize: "0.75rem",
                                width: "auto" 
                            }}
                        >
                            + Add Award
                        </button>
                    </Box>

                    {/* Award Rows - Two Column Grid */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(2, 1fr)" },
                            gap: { xs: 2.5, sm: 3 },
                        }}
                    >
                        {fields.map((field) => {
                            return (
                                <Box
                                    key={field.id}
                                    display="flex"
                                    flexDirection="column"
                                    gap={1.5}
                                    sx={{
                                        position: "relative",
                                        border: cardBorder,
                                        borderRadius: "5px",
                                        p: { xs: "12px", sm: "14px 16px" },
                                        background: cardBackground,
                                        boxShadow: cardShadow,
                                        backdropFilter: isDarkMode ? "blur(10px)" : "none",
                                        "&:hover": {
                                            borderColor: "#17b0a7",
                                        },
                                        transition: "all 0.22s ease",
                                    }}
                                >
                                    {/* X cancel — top-right corner, only on new unsaved rows */}
                                    {field.isNew && (
                                        <IconButton
                                            onClick={() => handleCancelNewField(field.id)}
                                            title="Cancel"
                                            sx={{
                                                position: "absolute",
                                                top: -10,
                                                right: -10,
                                                width: 22,
                                                height: 22,
                                                backgroundColor: "#e53935",
                                                color: "#fff",
                                                p: 0,
                                                "&:hover": { backgroundColor: "#c62828" },
                                                zIndex: 1,
                                            }}
                                        >
                                            <Close sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}

                                    {/* Row 1: Award Name + Action (inline) */}
                                    <Box 
                                        display="flex" 
                                        alignItems="flex-end" 
                                        gap={1.5}
                                    >
                                        <Box flex={1}>
                                            <p style={{ margin: "0 0 5px 2px", fontSize: "0.75rem", color: labelColor, fontWeight: 600 }}>Award Name</p>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Enter Award Name"
                                                value={field.award_name}
                                                onChange={(e) => handleFieldChange(field.id, "award_name", e.target.value)}
                                                onBlur={() => handleBlur(field.id)}
                                                autoFocus={field.isNew}
                                                disabled={!field.isNew && !field.isEditing}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        backgroundColor: !field.isNew && !field.isEditing ? inputDisabledBackground : inputBackground,
                                                        color: inputTextColor,
                                                        borderRadius: "5px",
                                                        transition: "all 0.2s ease",
                                                    },
                                                    "& .MuiOutlinedInput-input": {
                                                        color: inputTextColor,
                                                        fontWeight: 500,
                                                    },
                                                    "& .MuiOutlinedInput-input::placeholder": {
                                                        color: isDarkMode ? "#6f7c8f" : "#9ca3af",
                                                        opacity: 1,
                                                    },
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: inputBorderColor,
                                                    },
                                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#17b0a7" },
                                                    "& .MuiOutlinedInput-root.Mui-disabled": {
                                                        WebkitTextFillColor: inputTextColor,
                                                        opacity: 1,
                                                    },
                                                }}
                                            />
                                        </Box>

                                        {/* Action buttons — inline with row 1 */}
                                        <Box 
                                            display="flex" 
                                            alignItems="center" 
                                            gap={0.5} 
                                            sx={{ flexShrink: 0, pb: "2px" }}
                                        >
                                            {field.isNew ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveNewField(field.id)}
                                                    style={{
                                                        backgroundColor: "#17b0a7",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        padding: "6px 18px",
                                                        fontWeight: 600,
                                                        fontSize: "0.8rem",
                                                        cursor: "pointer",
                                                        height: 37,
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            ) : (
                                                <>
                                                    <Tooltip title="Edit Award">
                                                        <IconButton
                                                            onClick={() => handleEditClick(field.id)}
                                                            sx={{
                                                                color: editButtonColor,
                                                                p: { xs: "6px", sm: "8px" },
                                                                borderRadius: "50%",
                                                                backgroundColor: editButtonBackground,
                                                                "&:hover": {
                                                                    backgroundColor: isDarkMode ? "rgba(33, 150, 243, 0.2)" : "rgba(25, 118, 210, 0.08)",
                                                                },
                                                            }}
                                                            className="me-2"
                                                        >
                                                            <Edit sx={{ fontSize: { xs: 18, sm: 22 } }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Award">
                                                        <IconButton
                                                            onClick={() => handleDeleteClick(field)}
                                                            sx={{
                                                                color: deleteButtonColor,
                                                                p: { xs: "6px", sm: "8px" },
                                                                borderRadius: "50%",
                                                                backgroundColor: deleteButtonBackground,
                                                                "&:hover": {
                                                                    backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.2)" : "rgba(229, 57, 53, 0.08)",
                                                                },
                                                            }}
                                                        >
                                                            <Delete sx={{ fontSize: { xs: 18, sm: 22 } }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Row 2: Description — full width */}
                                     <Box>
                                        <p style={{ margin: "0 0 5px 2px", fontSize: "0.75rem", color: labelColor, fontWeight: 600 }}>Description</p>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter Award Description"
                                            value={field.description}
                                            onChange={(e) => handleFieldChange(field.id, "description", e.target.value)}
                                            onBlur={() => handleBlur(field.id)}
                                            disabled={!field.isNew && !field.isEditing}
                                            multiline
                                            minRows={1}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: !field.isNew && !field.isEditing ? inputDisabledBackground : inputBackground,
                                                    color: inputTextColor,
                                                    borderRadius: "5px",
                                                    transition: "all 0.2s ease",
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    color: inputTextColor,
                                                    fontWeight: 500,
                                                },
                                                "& .MuiOutlinedInput-input::placeholder": {
                                                    color: isDarkMode ? "#6f7c8f" : "#9ca3af",
                                                    opacity: 1,
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: inputBorderColor,
                                                },
                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#17b0a7" },
                                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                                    WebkitTextFillColor: inputTextColor,
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </Box>

                                </Box>

                            );
                        })}
                    </Box>

                    {/* Delete Confirmation Dialog */}
                    <Dialog
                        open={deleteDialog.open}
                        onClose={() => setDeleteDialog({ open: false, field: null })}
                        PaperProps={{
                            sx: {
                                background: isDarkMode ? "linear-gradient(180deg, #161c24 0%, #11161d 100%)" : "#fff",
                                color: isDarkMode ? "#f5f7fb" : "#111827",
                                boxShadow: isDarkMode ? "0 24px 60px rgba(0,0,0,0.42)" : undefined,
                            },
                        }}
                    >
                        <DialogTitle sx={{ color: isDarkMode ? "#f5f7fb" : "#111827" }}>Confirm Deletion</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ color: isDarkMode ? "#aab6c5" : "#4b5563" }}>
                                Are you sure you want to delete the award "{deleteDialog.field?.award_name}"?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialog({ open: false, field: null })}>No</Button>
                            <Button onClick={handleDelete} color="error">Yes</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </Box>
    );
};

export default Awards;
